import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface PaginationProps {
  total: number;
  limit: number;
  offset: number;
  onChange: (next: { limit: number; offset: number }) => void;
}

const PAGE_SIZES = [10, 20, 50, 100];

export function Pagination({ total, limit, offset, onChange }: PaginationProps) {
  const startIdx = total === 0 ? 0 : offset + 1;
  const endIdx = Math.min(offset + limit, total);
  const canPrev = offset > 0;
  const canNext = offset + limit < total;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-3 text-sm">
      <div className="text-muted-foreground">
        Showing{' '}
        <span className="font-medium text-foreground">
          {startIdx}
          {endIdx > startIdx ? `–${endIdx}` : ''}
        </span>{' '}
        of <span className="font-medium text-foreground">{total}</span>
      </div>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">Per page</span>
          <Select
            value={String(limit)}
            onValueChange={(v) => onChange({ limit: Number(v), offset: 0 })}
          >
            <SelectTrigger className="h-8 w-[80px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZES.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            disabled={!canPrev}
            onClick={() =>
              onChange({ limit, offset: Math.max(0, offset - limit) })
            }
            aria-label="Previous page"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            disabled={!canNext}
            onClick={() => onChange({ limit, offset: offset + limit })}
            aria-label="Next page"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
